import { useCallback, useEffect, useMemo, useState } from "react";

const DEFAULT_PAGE_SIZE_OPTIONS = [5, 10, 20, 25, 50, 100];

export const shouldShowDataTablePagination = ({
  canNext,
  canPrevious,
} = {}) => Boolean(canNext || canPrevious);

export const normalizePageSizeOptions = (
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  activePageSize,
) => {
  const options = Array.isArray(pageSizeOptions)
    ? pageSizeOptions
    : DEFAULT_PAGE_SIZE_OPTIONS;

  return Array.from(
    new Set(
      [...options, activePageSize]
        .map(Number)
        .filter((option) => Number.isFinite(option) && option > 0),
    ),
  ).sort((left, right) => left - right);
};

export default function useDataTablePagination({
  data,
  defaultPage,
  defaultPageSize,
  manual,
  onPageChange,
  onPageSizeChange,
  page,
  pageSize,
  pageSizeOptions,
}) {
  const [internalPagination, setInternalPagination] = useState({
    page: defaultPage,
    pageSize: defaultPageSize,
  });
  const isPageControlled = Number.isFinite(page);
  const isPageSizeControlled = Number.isFinite(pageSize);
  const currentPage = isPageControlled ? page : internalPagination.page;
  const currentPageSize = isPageSizeControlled
    ? pageSize
    : internalPagination.pageSize;
  const availablePageSizes = useMemo(
    () => normalizePageSizeOptions(pageSizeOptions, currentPageSize),
    [currentPageSize, pageSizeOptions],
  );

  const handlePageChange = useCallback(
    (nextPage) => {
      if (!isPageControlled)
        setInternalPagination((current) => ({
          ...current,
          page: nextPage,
        }));
      if (onPageChange) onPageChange(nextPage);
    },
    [isPageControlled, onPageChange],
  );

  const handlePageSizeChange = useCallback(
    (nextPageSize, nextPage) => {
      if (!isPageControlled || !isPageSizeControlled)
        setInternalPagination((current) => ({
          page: isPageControlled ? current.page : nextPage,
          pageSize: isPageSizeControlled ? current.pageSize : nextPageSize,
        }));
      if (onPageSizeChange) onPageSizeChange(nextPageSize, nextPage);
    },
    [
      isPageControlled,
      isPageSizeControlled,
      onPageSizeChange,
    ],
  );

  useEffect(() => {
    if (manual || isPageControlled || !Array.isArray(data)) return;

    const lastPage = Math.max(Math.ceil(data.length / currentPageSize) - 1, 0);
    if (internalPagination.page > lastPage)
      setInternalPagination((current) => ({ ...current, page: lastPage }));
  }, [
    currentPageSize,
    data,
    internalPagination.page,
    isPageControlled,
    manual,
  ]);

  return {
    availablePageSizes,
    currentPage,
    currentPageSize,
    handlePageChange,
    handlePageSizeChange,
    isPageControlled,
    isPageSizeControlled,
  };
}
